import { type FC } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'

export interface ImageProps extends DefaultProps {
  /**
   * Image Source specifies the image to display in the component
   */
  src: string

  /**
   * Alt text provides fallback (alternate) text to display when the image cannot be rendered
   */
  alt?: string

  /**
   * Image width
   */
  width?: number

  /**
   * Image height
   */
  height?: number

  /**
   * Image priority represents a hint given to the browser on how it should prioritize the fetch of the image
   */
  priority?: 'auto' | 'low' | 'high'

  /**
   * Loading: how to handle the loading of the image which is currently outside the window's viewport */
  loading?: 'eager' | 'lazy'
}

export const Image: FC<ImageProps> = ({
  className,
  id,
  testId,
  ariaLabel,
  src,
  alt,
  priority = 'auto',
  loading = 'lazy',
  width,
  height,
}) => {
  // Component base class names. Can be overriden by className property.
  const baseClass = cx({})

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  return (
    <img
      src={src}
      alt={alt}
      data-fetch-priority={priority}
      loading={loading}
      className={finalClasses}
      width={width}
      height={height}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      aria-hidden="true"
    />
  )
}
